import { useState, useMemo } from 'react';
import { generateLetterheadPDF } from '../../utils/letterheadPdf';

/**
 * ComposeLetter — FIG-level tool that generates a branded letterhead PDF via
 * src/utils/letterheadPdf.js. Real logo + leaf watermark are fetched at generate
 * time through the live token (same path the old window.__figLetterheadTest used).
 *
 * Phase 1: generate + browser-download only. Client / LOB / DocType are collected
 * and baked into the filename, and assembled into a `filingMeta` object, but
 * NOTHING is persisted to SharePoint yet — that filing/association layer is Phase 2.
 *
 * Props:
 *   getToken  — async token accessor
 *   insureds  — array of insured records (already loaded by FIGDashboard)
 */

// Static list for now (Phase 1). Anchors document filing in Phase 2.
const LOB_OPTIONS = [
  'Stock Throughput', 'Commercial Property', 'General Liability', 'Commercial Auto',
  'Umbrella/Excess', 'Workers Comp', 'E&O', 'BOP', 'Personal Lines', 'Other',
];

const DOC_TYPES = ['Submission', 'Cover Letter', 'Proposal', 'General Correspondence'];

const labelStyle = {
  display: 'block',
  fontFamily: "'Share Tech Mono', 'SF Mono', 'Consolas', monospace",
  fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1.5px',
  color: '#d4aa3e', textShadow: '0 0 4px rgba(212, 170, 62, 0.4)',
  textTransform: 'uppercase', marginBottom: '4px',
};
const rowStyle = { display: 'flex', gap: '0.6rem', flexWrap: 'wrap' };
const checkboxLabelStyle = {
  ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px',
  textTransform: 'none', marginBottom: 0, cursor: 'pointer',
};
const checkboxInputStyle = { accentColor: '#d4aa3e', width: '15px', height: '15px' };

// Slugify for filename: keep letters/digits only (e.g. "Umbrella/Excess" -> "UmbrellaExcess")
const slug = (v) => (v || '').replace(/[^a-zA-Z0-9]/g, '');

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ComposeLetter({ getToken, insureds = [] }) {
  const [insuredId, setInsuredId] = useState('');
  const [lob, setLob] = useState('');
  const [docType, setDocType] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientOrg, setRecipientOrg] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [includeAch, setIncludeAch] = useState(false);
  const [includeFrom, setIncludeFrom] = useState(true);
  const [includeDate, setIncludeDate] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const clientOptions = useMemo(
    () => [...insureds]
      .map(i => ({ id: String(i.id), name: i.CompanyName || i.Title || 'Unnamed' }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [insureds],
  );

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const client = insuredId ? insureds.find(i => String(i.id) === String(insuredId)) : null;
      const clientName = client ? (client.CompanyName || client.Title || '') : '';

      // Blank-line-separated paragraphs -> plain-string bodyBlocks (Phase 1: no headings UI)
      const bodyBlocks = body.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
      const addressLines = recipientAddress.split('\n').map(l => l.trim()).filter(Boolean);

      // Every field is optional — pass only what's filled. Empty fields are omitted
      // so the generator can skip those sections rather than render empty labels.
      const payload = { getToken, includeFrom, includeDate }; // getToken -> real logo + leaf; date defaults to today
      const recipientBlock = {};
      if (recipientName.trim()) recipientBlock.name = recipientName.trim();
      if (recipientOrg.trim()) recipientBlock.org = recipientOrg.trim();
      if (addressLines.length) recipientBlock.addressLines = addressLines;
      if (Object.keys(recipientBlock).length) payload.recipientBlock = recipientBlock;
      if (subject.trim()) payload.subjectLine = subject.trim();
      if (bodyBlocks.length) payload.bodyBlocks = bodyBlocks;
      if (includeAch) payload.includeAch = true;
      // signatureBlock omitted -> generator defaults to Marc Fragos / Fragos Insurance Group, LLC
      //                          (and skips the signature entirely when there's no body).

      const pdfBytes = await generateLetterheadPDF(payload);

      const ymd = todayYMD();
      const slugParts = [slug(clientName), slug(lob), slug(docType)].filter(Boolean);
      const filename = slugParts.length ? `${slugParts.join('_')}_${ymd}.pdf` : `FIG_Letterhead_${ymd}.pdf`;

      // Phase 2 filing metadata — captured + structured now, NOT yet persisted.
      const filingMeta = { insuredId, clientName, lob, docType, date: ymd, includeAch, filename };
      // eslint-disable-next-line no-console
      console.log('[ComposeLetter] filing metadata (Phase 2 will persist to SharePoint):', filingMeta);

      // Browser download — same mechanism as the invoice "INVOICE" action
      try {
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('[ComposeLetter] Download trigger failed:', err);
      }
    } catch (err) {
      setError(err.message || 'Failed to generate letter.');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="FIG-clients-pane">
      {error && <p className="FIG-error">{error}</p>}

      <div className="FIG-add-form" style={{ maxWidth: '760px' }}>
        {/* Letterhead toggles — control the always-on stationery elements */}
        <div style={{ display: 'flex', gap: '1.4rem', flexWrap: 'wrap', marginBottom: '2px' }}>
          <label style={checkboxLabelStyle}>
            <input type="checkbox" checked={includeFrom} onChange={e => setIncludeFrom(e.target.checked)} style={checkboxInputStyle} />
            Include FROM block
          </label>
          <label style={checkboxLabelStyle}>
            <input type="checkbox" checked={includeDate} onChange={e => setIncludeDate(e.target.checked)} style={checkboxInputStyle} />
            Include date
          </label>
        </div>

        {/* Client / LOB / Doc type */}
        <div>
          <label style={labelStyle}>Client</label>
          <select
            className="FIG-input"
            value={insuredId}
            onChange={e => setInsuredId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">{insureds.length ? 'Select a client…' : 'Loading clients…'}</option>
            {clientOptions.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div style={rowStyle}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <label style={labelStyle}>Line of Business</label>
            <select className="FIG-input" value={lob} onChange={e => setLob(e.target.value)} style={{ width: '100%' }}>
              <option value="">Select LOB…</option>
              {LOB_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <label style={labelStyle}>Document Type</label>
            <select className="FIG-input" value={docType} onChange={e => setDocType(e.target.value)} style={{ width: '100%' }}>
              <option value="">Select type…</option>
              {DOC_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        {/* Recipient */}
        <div style={rowStyle}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <label style={labelStyle}>Recipient Name</label>
            <input className="FIG-input" type="text" value={recipientName}
              onChange={e => setRecipientName(e.target.value)} placeholder="Alan de la Houssaye" style={{ width: '100%' }} />
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <label style={labelStyle}>Recipient Org</label>
            <input className="FIG-input" type="text" value={recipientOrg}
              onChange={e => setRecipientOrg(e.target.value)} placeholder="CRC Group" style={{ width: '100%' }} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Recipient Address <span style={{ opacity: 0.6 }}>(optional, one line each)</span></label>
          <textarea className="FIG-input" value={recipientAddress}
            onChange={e => setRecipientAddress(e.target.value)}
            rows={2} placeholder={'123 Market St, Suite 400\nHouston, TX 77002'}
            style={{ width: '100%', resize: 'vertical', fontFamily: "'Share Tech Mono', monospace" }} />
        </div>

        {/* Subject */}
        <div>
          <label style={labelStyle}>Subject Line</label>
          <input className="FIG-input" type="text" value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="RE: Green Span Profiles LP — Stock Throughput Renewal" style={{ width: '100%' }} />
        </div>

        {/* Body */}
        <div>
          <label style={labelStyle}>Body <span style={{ opacity: 0.6 }}>(separate paragraphs with a blank line)</span></label>
          <textarea className="FIG-input" value={body}
            onChange={e => setBody(e.target.value)}
            rows={12} placeholder={'Type or paste the letter body here.\n\nLeave a blank line between paragraphs.'}
            style={{ width: '100%', resize: 'vertical', lineHeight: 1.5 }} />
        </div>

        {/* ACH */}
        <label style={checkboxLabelStyle}>
          <input type="checkbox" checked={includeAch} onChange={e => setIncludeAch(e.target.checked)} style={checkboxInputStyle} />
          Include ACH / Wire instructions block
        </label>

        <button className="FIG-save-btn" onClick={handleGenerate} disabled={generating} style={{ marginTop: '4px' }}>
          {generating ? 'Generating…' : 'Generate Letter'}
        </button>
      </div>
    </div>
  );
}
